import { useContext, useState, useEffect } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { formatDate } from "@aclymatepackages/formatters";
import { STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES } from "@aclymatepackages/constants";

import { useAccountData, useCachedDisplayData } from "../firebase";
import useEmissionsContext from "../contexts/emissions";
import { StripeCustomerContext } from "../contexts/stripeCustomerContext";
import { fetchOurApi } from "../utils/apiCalls";
import usePlatformLayoutContext from "../contexts/platformLayout";
import { AdminDataContext } from "../contexts/adminDataContext";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);

export const useCompletedOnboardingSteps = () => {
  const { isSimulation } = usePlatformLayoutContext() || {};
  const [
    { isRemote, employeeCountConfirmed, employeeSurveyLinkSent },
    companyDataLoading
  ] = useAccountData();
  const { paymentMethod, isPaymentMethodLoading } =
    useContext(StripeCustomerContext) || {};
  const { card } = paymentMethod || {};

  const [offices, officesLoading] = useCachedDisplayData("offices");
  const {
    vendors = [],
    employees = [],
    emissionsLoading
  } = useEmissionsContext();
  const [offsets, offsetsLoading] = useCachedDisplayData("offsets");

  const dataLoading =
    companyDataLoading ||
    isPaymentMethodLoading ||
    officesLoading ||
    emissionsLoading ||
    offsetsLoading;

  const completedOnboardingSteps = isSimulation
    ? {
        officesComplete: true,
        vendorsComplete: true,
        employeesComplete: true,
        billingComplete: true,
        offsetsComplete: true
      }
    : {
        officesComplete: !!offices.length || isRemote,
        vendorsComplete: !!vendors.length,
        employeesComplete:
          !!employees.length ||
          (employeeCountConfirmed && employeeSurveyLinkSent),
        billingComplete: !!card,
        offsetsComplete: !!offsets.length
      };

  return [completedOnboardingSteps, dataLoading];
};

export const useSubscriptionType = (subscriptionType = "saas") => {
  const {
    subscriptions = [],
    isSubscriptionsLoading,
    isNotStripeCustomer
  } = useContext(StripeCustomerContext) || {};

  const subscriptionObj = subscriptions.find(
    ({ product }) => product === subscriptionType
  );

  // Only the main SaaS subscription and the PCF add-on are meant to be
  // always-on in the demo environment -- offset-bucket products
  // (naturalAndWorkingLands/cleanTransition/climateJustice, via
  // useOffsetBucketSubscription) and any other product this hook is called
  // for must keep reflecting their real subscription state.
  const isAlwaysOnDemoSubscriptionType =
    process.env.REACT_APP_ENVIRONMENT === "demo" &&
    (subscriptionType === "saas" ||
      subscriptionType === "productCarbonFootprinting");

  // A company with no stripeCustomerId never even reaches the backend's
  // subscriptions endpoint, so subscriptionObj is never populated here
  // (unlike a company that has one, where the demo-aware
  // getCustomerSubscriptions on the backend already supplies a real-shaped
  // object). Settings -> Billing's SubscriptionDisplayCard looks up a
  // matching product/tier by type and crashes on a miss, so this must
  // always resolve to a real, known tier (plus a real-shaped
  // upcomingInvoice) whenever status is forced active with nothing real to
  // describe.
  const demoFallbackSubscriptionShape =
    isAlwaysOnDemoSubscriptionType && !subscriptionObj
      ? {
          type: subscriptionType === "saas" ? "concierge" : subscriptionType,
          interval: "year",
          upcomingInvoice: {
            dollarAmountDue: 0,
            dueDate: dayjs().add(1, "year").toDate()
          }
        }
      : {};

  const {
    status: backendStatus,
    upcomingInvoice,
    trialDate
  } = subscriptionObj || demoFallbackSubscriptionShape;
  const { dueDate } = upcomingInvoice || {};

  const findSubscriptionStatus = () => {
    if (isSubscriptionsLoading) {
      return null;
    }

    // The demo environment is an isolated project with no real customers,
    // vendors, or billing at stake -- every company doc in it, including any
    // vendor/Explorer-shaped stub created while demoing those features, is
    // synthetic. So this applies unconditionally there, with no account-type
    // exception (unlike every other environment, where isNotStripeCustomer
    // below is the real, load-bearing signal for a genuine Explorer account).
    if (isAlwaysOnDemoSubscriptionType) {
      return "active";
    }

    if (isNotStripeCustomer) {
      return "vendor";
    }

    if (subscriptionType !== "saas") {
      return backendStatus;
    }

    const knownFrontendStatuses = ["active", "expired", "canceled", "trialing"];
    if (knownFrontendStatuses.includes(backendStatus)) {
      return backendStatus;
    }

    return "expired";
  };

  const isTestEnvironment = process.env.REACT_APP_ENVIRONMENT === "testing";

  const applyTestEnvironmentOverride = (computedStatus) => {
    if (
      isTestEnvironment &&
      subscriptionType === "saas" &&
      computedStatus === "expired"
    ) {
      return "active";
    }
    return computedStatus;
  };

  const status = applyTestEnvironmentOverride(findSubscriptionStatus());

  return [
    {
      ...subscriptionObj,
      ...demoFallbackSubscriptionShape,
      upcomingInvoice,
      status,
      isPaid: status === "active",
      cancellationDate: formatDate(dueDate),
      trialDate
    },
    isSubscriptionsLoading
  ];
};

export const useShowOnboardingPage = () => {
  const [
    { officesComplete, vendorsComplete, employeesComplete },
    completedOnboardingLoading
  ] = useCompletedOnboardingSteps();

  const showOnboardingPage = !(
    officesComplete &&
    vendorsComplete &&
    employeesComplete
  );

  return [showOnboardingPage, completedOnboardingLoading];
};

export const useOffsetBucketSubscription = () => {
  const [
    naturalAndWorkingLandsSubscription,
    naturalAndWorkingLandsSubscriptionLoading
  ] = useSubscriptionType("naturalAndWorkingLands");
  const [cleanTransitionSubscription, cleanTransitionSubscriptionLoading] =
    useSubscriptionType("cleanTransition");
  const [climateJusticeSubscription, climateJusticeSubscriptionLoading] =
    useSubscriptionType("climateJustice");

  const findSubscription = () => {
    if (naturalAndWorkingLandsSubscription?.status) {
      return naturalAndWorkingLandsSubscription;
    }
    if (cleanTransitionSubscription?.status) {
      return cleanTransitionSubscription;
    }
    if (climateJusticeSubscription?.status) {
      return climateJusticeSubscription;
    }
    return {};
  };

  return [
    findSubscription(),
    naturalAndWorkingLandsSubscriptionLoading ||
      cleanTransitionSubscriptionLoading ||
      climateJusticeSubscriptionLoading
  ];
};

export const useStarterTierSubscriptionFlags = () => {
  const [{ type }] = useSubscriptionType("saas");
  const [employees] = useCachedDisplayData("employees");

  const isStarterTier = type === "starter";

  const willEmployeeLimitBeReachedFromNewEmployees = (newEmployees) =>
    isStarterTier &&
    employees.length + newEmployees.length >
      STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES;

  return {
    isStarterTier,
    isEmployeeLimitReached:
      isStarterTier &&
      employees.length >= STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES,
    willEmployeeLimitBeReachedFromNewEmployees
  };
};

export const useRevenueYears = () => {
  const [{ startDate, revenueYears: dbRevenueYears = [] }, accountDataLoading] =
    useAccountData();

  const startYear = dayjs(startDate).year();
  const lastCompleteYear = dayjs().year() - 1;

  const revenueYears = [...new Array(lastCompleteYear - startYear + 1)].map(
    (_, idx) => {
      const year = startYear + idx;
      const { revenue } =
        dbRevenueYears.find(({ year: revenueYear }) => revenueYear === year) ||
        {};

      return { year, revenue };
    }
  );

  return [revenueYears, accountDataLoading];
};

export const useCompanyAuditLogs = () => {
  const [{ id: companyId }, accountDataLoading] = useAccountData();
  const { viewMode } = usePlatformLayoutContext() || {};
  const { adminData = {} } = useContext(AdminDataContext) || {};

  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = viewMode === "admin";
  const companyAuditLogs = isAdmin ? adminData["audit-logs"] : auditLogs;
  const isCompanyAuditLogsLoading = isAdmin ? false : isLoading;

  useEffect(() => {
    if (!companyId || accountDataLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    fetchOurApi({
      path: "/companies/fetch-audit-logs",
      method: "POST",
      data: { companyId },
      callback: (logs) => {
        if (Array.isArray(logs) && logs.length > 0) {
          setAuditLogs(logs);
        }

        return setIsLoading(false);
      },
      errorCallback: (err) => {
        setError(err);

        return setIsLoading(false);
      }
    });
  }, [companyId, accountDataLoading]);

  return [companyAuditLogs, isCompanyAuditLogsLoading, error];
};
